import React, { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { isAthlete, isGuardian, getTermsAccepted } from "../utils/auth";
import { getGuardianViewingAthleteId } from "../utils/viewedAthlete";
import type { AthleteAdminNavState } from "./AthleteAdminBreadcrumbNav";
import { API_BASE_URL } from "../utils/apiConfig";
import { useResourceVisibility } from "../hooks/useResourceVisibility";
import { useAssistAccess } from "../hooks/useAssistAccess";
import { useUserAvatar } from "../utils/profilePictureUtils";
import RailSidebarShell from "./RailSidebarShell";
import { shouldIncludeOnboardingNavItem } from "./athleteSidebarNav";
import "../styles/sidebar.css";

// Import all nav icons - active and inactive states
import OnboardingIcon from "../images/nav-icons/Onboarding-inactive.svg";
import OnboardingActiveIcon from "../images/nav-icons/Onboarding-active.svg";
import ProfileIcon from "../images/nav-icons/Athlete-profile-inactive.svg";
import ProfileActiveIcon from "../images/nav-icons/Athlete-profile-active.svg";
import CollegePlanningIcon from "../images/nav-icons/College-Planning-inactive.svg";
import NcaaTrackerIcon from "../images/ncaa-tracker/NCAA-tracker.svg";
import NcaaTrackerActiveIcon from "../images/ncaa-tracker/NCAA-tracker-active.svg";
import CollegePlanningActiveIcon from "../images/nav-icons/College-Planning-active.svg";
import NILIcon from "../images/nav-icons/NIL-inactive.svg";
import NILActiveIcon from "../images/nav-icons/NIL-active.svg";
import ConnectIcon from "../images/nav-icons/Connect-inactive.svg";
import ConnectActiveIcon from "../images/nav-icons/Connect-active.svg";
import GamePlanIcon from "../images/nav-icons/Game-plan-inactive.svg";
import GamePlanActiveIcon from "../images/nav-icons/Game-Plan-active.svg";
import DiscoverIcon from "../images/nav-icons/DISCOVER U-inactive.svg";
import DiscoverActiveIcon from "../images/nav-icons/DISCOVER U-active.svg";
import SpotlightIcon from "../images/nav-icons/Spotlight inactive.svg";
import SpotlightActiveIcon from "../images/nav-icons/Spotlight-active.svg";
import SessionsIcon from "../images/nav-icons/Sessions-gray.svg";
import SessionsActiveIcon from "../images/nav-icons/sessions-active.svg";
import TutorTestPrepIcon from "../images/nav-icons/tutor-test-prep.svg";
import TutorTestPrepActiveIcon from "../images/nav-icons/tutor-test-prep-active.svg";
import ProgramIcon from "../images/program/program-icon.png";
import AdvisingIcon from "../images/nav-icons/Polaris-advising star-nav.svg";
import AdvisingActiveIcon from "../images/nav-icons/Polaris-advising star-white (1).svg";
import googleDriveIcon from "../images/nav-icons/google-drive.svg";
import googleCalendarIcon from "../images/nav-icons/Calendar_Google_Default.png";
import AiAssistantActiveIcon from "../images/nav-icons/Polaris-Assist-black-star.svg";
import AiAssistantRailActiveIcon from "../images/nav-icons/Polaris-Assist-rail-active.svg";
import AiAssistantInactiveIcon from "../images/nav-icons/Polaris-Assist-inactive.svg";


// ICONS object with active and inactive states
const ICONS = {
    home: { active: OnboardingActiveIcon, inactive: OnboardingIcon },
    profile: { active: ProfileActiveIcon, inactive: ProfileIcon },
    collegeplan: { active: CollegePlanningActiveIcon, inactive: CollegePlanningIcon },
    ncaa: { active: NcaaTrackerActiveIcon, inactive: NcaaTrackerIcon },
    niledu: { active: NILActiveIcon, inactive: NILIcon },
    recruiting: { active: ConnectActiveIcon, inactive: ConnectIcon },
    gameplan: { active: GamePlanActiveIcon, inactive: GamePlanIcon },
    discover: { active: DiscoverActiveIcon, inactive: DiscoverIcon },
    spotlight: { active: SpotlightActiveIcon, inactive: SpotlightIcon },
    calendar: { active: googleCalendarIcon, inactive: googleCalendarIcon },
    drive: { active: googleDriveIcon, inactive: googleDriveIcon },
    tutoring: { active: TutorTestPrepActiveIcon, inactive: TutorTestPrepIcon },
    program: { active: ProgramIcon, inactive: ProgramIcon },
    advising: { active: AdvisingActiveIcon, inactive: AdvisingIcon },
    sessions: { active: SessionsActiveIcon, inactive: SessionsIcon },
    aiAssistant: { active: AiAssistantActiveIcon, inactive: AiAssistantInactiveIcon },
};

type NavItem = {
    id: string;
    label: string;
    iconActive: string;
    iconInactive: string;
    path?: string;
    isActive?: boolean;
};

export type AthleteSidebarProps = {
    variant?: "athlete" | "guardian-rail" | "admin-secondary";
    companyLogoUrl?: string | null;
    /** Remount key so a replaced logo reloads even if the URL string is unchanged. */
    companyLogoVersion?: number;
    footerAvatarUrl?: string | null;
    onCompanyLogoError?: () => void;
    /** When false, hides the onboarding/home nav item for athletes viewing their own account. */
    showOnboardingNav?: boolean;
};

export default function AthleteSidebar({
    variant = "athlete",
    companyLogoUrl = null,
    companyLogoVersion = 0,
    footerAvatarUrl = null,
    onCompanyLogoError,
    showOnboardingNav = true,
}: AthleteSidebarProps) {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const location = useLocation();
    const isAdminSecondary = variant === "admin-secondary";
    const isAdminViewingAthlete = !isAthlete() && !isGuardian() && !!athleteId;
    const isGuardianRail = variant === "guardian-rail";
    const isAthleteWideRail = isAthlete() && !isAdminViewingAthlete && variant === "athlete";
    const useRailShell = isGuardianRail || isAthleteWideRail;
    const [athleteName, setAthleteName] = useState<string>("Athlete Profile");
    const [athleteUserId, setAthleteUserId] = useState<number | undefined>(undefined);
    const [hasSessions, setHasSessions] = useState<boolean>(false);
    const [athleteEmail, setAthleteEmail] = useState<string | null>(null);
    // Bumped when the athlete accepts the Terms & Conditions so the name/sessions refetch in place
    // instead of forcing a jarring full-page reload.
    const [termsRefreshKey, setTermsRefreshKey] = useState(0);
    const { visibility: resourceVisibility } = useResourceVisibility();
    // Fail open while loading or on fetch failure so nav is never hidden by accident.
    const recruitingResourcesEnabled = resourceVisibility?.recruitingResourcesEnabled !== false;
    const { visible: showAssist } = useAssistAccess();
    const { avatarUrl: adminViewAvatarUrl } = useUserAvatar(
        isAdminViewingAthlete ? athleteUserId : undefined,
    );

    // Get team member info from navigation state to preserve it across navigation
    const navState = location.state as AthleteAdminNavState | null;

    // Fetch athlete display name (GET /api/athletes/{id} expects athlete profile id, not user id)
    useEffect(() => {
        // Only fetch if terms have been accepted (for athlete viewing their own profile)
        // OR if an admin is viewing the athlete's profile (athleteId is present)
        if (!athleteId && !isAdminViewingAthlete) {
            const hasAcceptedTerms = getTermsAccepted();
            if (!hasAcceptedTerms) {
                return;
            }
        }

        let cancelled = false;

        (async () => {
            let url: string | null = null;

            if (athleteId) {
                url = `${API_BASE_URL}/api/athletes/${encodeURIComponent(athleteId)}`;
            } else if (isAthlete()) {
                url = `${API_BASE_URL}/api/athletes/me`;
            } else if (isGuardian()) {
                const guardianAthleteId = localStorage.getItem("guardianViewingAthleteId");
                if (!guardianAthleteId?.trim()) return;
                url = `${API_BASE_URL}/api/athletes/id/${encodeURIComponent(guardianAthleteId.trim())}`;
            } else {
                return;
            }

            try {
                const res = await fetch(url, {
                    method: "GET",
                    headers: { Accept: "application/json" },
                    credentials: "include" // Use HTTP-only cookie for auth
                });

                if (!res.ok) return;
                const data: any = await res.json();

                const displayName: string =
                    data?.displayName ||
                    [data?.firstName, data?.lastName].filter(Boolean).join(" ") ||
                    "Athlete Profile";

                if (!cancelled) {
                    setAthleteName(displayName);
                    setAthleteEmail(data?.email || data?.username || null);
                    setAthleteUserId(typeof data?.userId === "number" ? data.userId : undefined);
                }
            } catch {
                // ignore
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [athleteId, isAdminViewingAthlete, termsRefreshKey]);

    // Check if athlete has sessions
    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const headers: HeadersInit = { Accept: "application/json" };

                let targetAthleteId: string;

                if (athleteId) {
                    // Admin viewing athlete's profile - use athleteId from URL
                    targetAthleteId = athleteId;
                } else if (isAthlete()) {
                    // Athlete viewing their own profile - fetch from /api/athletes/me
                    const meRes = await fetch(`${API_BASE_URL}/api/athletes/me`, {
                        method: "GET",
                        headers,
                        credentials: "include" // Send HTTP-only cookies
                    });
                    if (!meRes.ok) return;

                    const athleteData = await meRes.json();
                    if (!athleteData?.id) return;

                    targetAthleteId = String(athleteData.id);

                    // Store athleteId in localStorage for future use
                    localStorage.setItem('athleteId', targetAthleteId);
                } else if (isGuardian()) {
                    const guardianAthleteId = getGuardianViewingAthleteId();
                    if (!guardianAthleteId) return;
                    targetAthleteId = guardianAthleteId;
                } else {
                    // Not an athlete and no athleteId in URL
                    return;
                }

                // Fetch sessions for the athlete
                const url = `${API_BASE_URL}/api/athletes/${encodeURIComponent(targetAthleteId)}/sessions`;
                const res = await fetch(url, {
                    method: "GET",
                    headers,
                    credentials: "include" // Send HTTP-only cookies
                });

                if (res.ok) {
                    const sessions = await res.json();
                    if (!cancelled) {
                        setHasSessions(Array.isArray(sessions) && sessions.length > 0);
                    }
                }
            } catch (error) {
                // ignore
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [athleteId, termsRefreshKey]);

    // Listen for termsAccepted event to refresh athlete name after terms are accepted.
    // Refresh the name/sessions in place rather than a full-page reload, which (combined with the
    // home page's in-place reload) made the screen visibly jitter with multiple mini reloads.
    useEffect(() => {
        const handleTermsAccepted = () => {
            setTermsRefreshKey((key) => key + 1);
        };

        window.addEventListener('termsAccepted', handleTermsAccepted);
        return () => window.removeEventListener('termsAccepted', handleTermsAccepted);
    }, []);

    // Dynamic path generator for athlete-specific routes
    const getAthletePath = (basePath: string) => {
        if (isAdminViewingAthlete && athleteId) {
            return `${basePath}/${athleteId}`;
        }
        return basePath;
    };

    const primaryItems: NavItem[] = [
        ...(shouldIncludeOnboardingNavItem(isAthlete(), isAdminViewingAthlete, showOnboardingNav)
            ? [{ id: "home", label: "Onboarding", iconActive: ICONS.home.active, iconInactive: ICONS.home.inactive, path: getAthletePath("/athlete/home") }]
            : []),
        { id: "profile", label: athleteName, iconActive: ICONS.profile.active, iconInactive: ICONS.profile.inactive, path: getAthletePath("/athlete/profile") },
        { id: "ncaa", label: "NCAA Tracker", iconActive: ICONS.ncaa.active, iconInactive: ICONS.ncaa.inactive, path: getAthletePath("/athlete/ncaa") },
        { id: "program", label: "Hub", iconActive: ICONS.program.active, iconInactive: ICONS.program.inactive, path: getAthletePath("/athlete/program") },
        { id: "collegeplan", label: "College Planning", iconActive: ICONS.collegeplan.active, iconInactive: ICONS.collegeplan.inactive, path: getAthletePath("/athlete/college-planning") },
        { id: "niledu", label: "NIL", iconActive: ICONS.niledu.active, iconInactive: ICONS.niledu.inactive, path: getAthletePath("/athlete/nil-edu") },
        ...(recruitingResourcesEnabled
            ? [{ id: "recruiting", label: "Recruiting", iconActive: ICONS.recruiting.active, iconInactive: ICONS.recruiting.inactive, path: getAthletePath("/athlete/recruiting") }]
            : []),
        { id: "gameplan", label: "Game Plan", iconActive: ICONS.gameplan.active, iconInactive: ICONS.gameplan.inactive, path: getAthletePath("/athlete/game-plan") },
        ...(!isAthlete() || hasSessions ? [{ id: "sessions", label: "Sessions", iconActive: ICONS.sessions.active, iconInactive: ICONS.sessions.inactive, path: getAthletePath("/athlete/sessions") }] : []),
        { id: "tutoring", label: "Tutoring & Test Prep", iconActive: ICONS.tutoring.active, iconInactive: ICONS.tutoring.inactive, path: getAthletePath("/athlete/tutoring") },
        ...(!isAdminSecondary && showAssist
            ? [{ id: "ai-assistant", label: "Polaris Assist", iconActive: ICONS.aiAssistant.active, iconInactive: ICONS.aiAssistant.inactive, path: "/ai-assistant" }]
            : []),
    ];

    const utilityItems: NavItem[] = !isAdminViewingAthlete
        ? [
            { id: "calendar", label: "Calendar", iconActive: ICONS.calendar.active, iconInactive: ICONS.calendar.inactive, path: getAthletePath("/calendar") },
            { id: "drive", label: "Drive", iconActive: ICONS.drive.active, iconInactive: ICONS.drive.inactive, path: getAthletePath("/drive") },
        ]
        : [];

    const renderNavLink = (it: NavItem, shouldHaveDivider: boolean) => {
        const isCurrentPath =
            it.id === "home"
                ? location.pathname.startsWith("/athlete/home")
                : it.id === "ncaa"
                ? location.pathname.startsWith("/athlete/ncaa")
                : location.pathname === it.path;
        const isActive = isCurrentPath;
        const linkClass = isActive ? "side-link active" : "side-link";
        const iconToDisplay = isActive
            ? it.id === "ai-assistant" && useRailShell
                ? AiAssistantRailActiveIcon
                : it.id === "ai-assistant" && isAdminViewingAthlete
                    ? AiAssistantActiveIcon
                    : it.iconActive
            : it.iconInactive;

        const useProfilePhoto = it.id === "profile" && isAdminViewingAthlete && Boolean(adminViewAvatarUrl);
        const profileImgClass = `side-img side-img--profile-photo${isActive ? " side-img--profile-photo-active" : ""}`;

        return (
            <Link
                key={it.id}
                to={it.path || "#"}
                state={{
                    ...navState,
                    athleteEmail: athleteEmail || navState?.athleteEmail,
                    ...(athleteName && athleteName !== "Athlete Profile"
                        ? { athleteDisplayName: athleteName }
                        : {}),
                }}
                className={linkClass}
                title={it.label}
                data-testid={`sidebar-${it.id}`}
                data-divider-after={shouldHaveDivider ? "true" : undefined}
            >
                <div className="side-ico">
                    {useProfilePhoto ? (
                        <img
                            src={adminViewAvatarUrl!}
                            alt=""
                            className={profileImgClass}
                        />
                    ) : (
                        <img
                            src={iconToDisplay}
                            alt=""
                            className={
                                it.id === "program"
                                    ? `side-img program-nav-icon${isActive ? " program-nav-icon--active" : ""}${useRailShell && isActive ? " side-img--fullcolor" : ""}`
                                    : it.id === "calendar" || it.id === "drive"
                                        ? `side-img${useRailShell ? " side-img--fullcolor" : ""}`
                                        : it.id === "ai-assistant" && (useRailShell || isAdminViewingAthlete) && isActive
                                            ? "side-img side-img--fullcolor"
                                            : "side-img"
                            }
                        />
                    )}
                </div>
                <div className="side-label">{it.label}</div>
            </Link>
        );
    };

    const nav = useRailShell ? (
        <div className="sidebar-rail-nav-inner">
            <nav className="side-nav side-nav--primary">
                {primaryItems.map((it) => renderNavLink(it, it.id === "sessions"))}
            </nav>
            {utilityItems.length > 0 && (
                <nav className="side-nav side-nav--utility">
                    {utilityItems.map((it) => renderNavLink(it, false))}
                </nav>
            )}
        </div>
    ) : (
        <nav className="side-nav">
            {[...primaryItems, ...utilityItems].map((it) => renderNavLink(it, it.id === "sessions"))}
        </nav>
    );

    if (isGuardianRail) {
        return (
            <RailSidebarShell
                headerLogoUrl={companyLogoUrl}
                headerLogoKey={companyLogoVersion}
                footerAvatarUrl={footerAvatarUrl}
                footerAvatarAlt="Athlete profile"
                confirmLogout
                onHeaderLogoError={onCompanyLogoError}
                className="athlete-primary-sidebar athlete-wide-rail"
                data-testid="guardian-rail-sidebar"
            >
                {nav}
            </RailSidebarShell>
        );
    }

    if (isAthleteWideRail) {
        return (
            <RailSidebarShell
                headerLogoUrl={companyLogoUrl}
                headerLogoKey={companyLogoVersion}
                footerAvatarUrl={footerAvatarUrl}
                footerAvatarAlt="Your profile"
                confirmLogout
                onHeaderLogoError={onCompanyLogoError}
                className="athlete-primary-sidebar athlete-wide-rail"
                data-testid="athlete-rail-sidebar"
            >
                {nav}
            </RailSidebarShell>
        );
    }

    return (
        <aside className="sidebar athlete-primary-sidebar">
            {nav}
        </aside>
    );
}
